import { Badge, BadgeShape, Image, type ImageProps, ScoreBar } from '@vizit/atoms';
import { BadgeLevel, BadgeSize } from '@vizit/atoms';
import { ImageSparkIcon } from '@vizit/icons';
import { colors, cva, cx } from '@vizit/theme';
import { hasScore, serializeScore } from '@vizit/utils';
import React from 'react';
import { AssetContext } from './asset.context-provider';

interface AssetImageProps extends ImageProps {
  /**
   * Optional ImageProps passed directly to `Image`
   */
}

const styles = {
  root: cva({
    base: 'inline-flex select-none flex-col gap-1 w-full',
    variants: {
      isSelected: {
        true: 'ring-2 ring-gray-modern-500 ring-offset-1 ring-offset-white',
        false: '',
      },
    },
    defaultVariants: {
      isSelected: false,
    },
  }),
  imageContainer: cva({
    base: 'relative',
  }),
};

function AssetImage(imageProps: AssetImageProps) {
  const { className, name, src, ...otherImageProps } = imageProps;

  // score has been validated in the context provider
  const {
    isLoading: isLoadingContext,
    isUnscored,
    unscoredLabel,
    isScoreLoading,
    isViewOnly,
    score,
    isSelected,
    isHovered,
    showScoreBar = true,
    slotNumber,
    isAiGenerated = false,
  } = React.useContext(AssetContext);

  const isLoading = isScoreLoading || isLoadingContext || (!hasScore(score) && !isUnscored);

  const showEmptyPlaceholder = isViewOnly && !src;

  return (
    <div className={cx(styles.root({ isSelected }), className)}>
      <div className={styles.imageContainer()}>
        {showEmptyPlaceholder ? (
          <div
            className="aspect-square w-full border border-primary bg-secondary-subtle"
            role="img"
            aria-label="Empty asset placeholder"
          >
            <svg className="h-full w-full" preserveAspectRatio="none" aria-hidden="true">
              <line x1="0" y1="0" x2="100%" y2="100%" stroke={colors['gray-utility']['200']} strokeWidth="1" />
              <line x1="100%" y1="0" x2="0" y2="100%" stroke={colors['gray-utility']['200']} strokeWidth="1" />
            </svg>
          </div>
        ) : (
          <>
            <Image isUploading={isLoadingContext} name={name} src={src} contain={false} {...otherImageProps} />
            {slotNumber && (
              <div className="absolute top-2 left-3 z-10">
                <Badge level={BadgeLevel.Slot} size={BadgeSize.Sm} shape={BadgeShape.Box}>
                  {slotNumber}
                </Badge>
              </div>
            )}
            {isAiGenerated && (
              <div
                className="absolute bottom-4 right-4 z-10 bg-gray-modern-900 p-1"
                role="img"
                aria-label="AI-generated image"
              >
                <ImageSparkIcon isActive size={20} color="white" aria-hidden="true" />
              </div>
            )}
          </>
        )}
      </div>
      {showScoreBar &&
        (showEmptyPlaceholder ? (
          <div className="h-[29px] w-full rounded-[1px] bg-primary-hover" />
        ) : (
          <ScoreBar
            score={hasScore(score) && !isUnscored ? Number(serializeScore(score)) : null}
            isHovered={isHovered && !isLoadingContext && !isScoreLoading}
            isLoading={isLoading}
            isUnscored={!!isUnscored}
            unscoredLabel={unscoredLabel}
          />
        ))}
    </div>
  );
}

export type { AssetImageProps };
export { AssetImage };
